import { Icon } from '../../icons';
import CloverIcon from '../ui/CloverIcon';
import Avatar from '../ui/Avatar';
import { NAV } from '../../constants/nav';
import { usePermissions } from '../../hooks/usePermissions';
import { useSubscription } from '../../hooks/useSubscription';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

/* ── SidebarContent: Extracted for better scoping ── */
const SidebarContent = ({
  t,
  forMobile = false,
  collapsed,
  onNav,
  onMobileClose,
  userName,
  userRole,
  onLogout,
  active,
  hasPermission,
  hasFeature,
  user
}) => {
  const handleNav = (id) => {
    onNav(id);
    if (onMobileClose) onMobileClose();
  };

  return (
    <nav
      className={`
      ${forMobile ? "w-72" : collapsed ? "w-16" : "w-60"}
      bg-ink dark:bg-slate-950 flex flex-col h-full overflow-hidden transition-all duration-200 relative border-r dark:border-slate-800
    `}
      style={forMobile ? { paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' } : undefined}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at top left,rgba(79,70,229,0.15) 0%,transparent 60%)" }} />

      {/* Logo row */}
      <div className={`h-14 flex items-center gap-3 px-4 border-b border-white/5 dark:border-slate-800 relative ${!forMobile && collapsed ? "justify-center" : ""}`}>
        <CloverIcon width={32} height={32} className="w-8 h-8 flex-shrink-0" />
        {(forMobile || !collapsed) && (
          <div className="flex-1 min-w-0">
            <div className="text-white text-sm font-bold leading-tight">{t("app_title")}</div>
            <div className="text-white/40 text-sm font-medium">{t("app_subtitle")}</div>
          </div>
        )}
        {/* Close button - mobile only */}
        {forMobile && onMobileClose && (
          <button
            onClick={onMobileClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-white/50 hover:text-white transition-colors bg-transparent border-0 cursor-pointer flex-shrink-0"
          >
            <Icon name="x" size={16} />
          </button>
        )}
      </div>

      {/* Nav items */}
      <div className="flex-1 overflow-y-auto py-3 space-y-0.5 dark-scrollbar">
        {NAV.map((g, gi) => {
          const isAdmin = user?.roles_list?.some(r => ['super_admin', 'admin'].includes(r));
  const isMember = (user?.primary_role_label === 'Member' || user?.roles_list?.includes('member')) && !isAdmin;
          const visibleItems = g.items.filter(item => {
            if (isMember && (item.id === 'documents' || item.id === 'committee' || item.id === 'vendors' || item.id === 'dashboard')) return false;

            // Check Entitlements first
            if (item.feature && !hasFeature(item.feature)) return false;

            // Then Check RBAC Permissions
            return !item.permission || hasPermission(item.permission);
          });
          if (visibleItems.length === 0) return null;

          return (
            <div key={gi} className="mb-1">
              {(forMobile || !collapsed) && (
                <div className="px-4 py-1.5 text-sm font-bold text-white/25 uppercase tracking-widest">{t(g.sec)}</div>
              )}
              {visibleItems.map(item => {
                const on = active === item.id;
                return (
                  <div
                    key={item.id}
                    onClick={() => handleNav(item.id)}
                    className={`
                      nav-item flex items-center gap-3 mx-2 px-3 rounded-xl cursor-pointer transition-all text-sm min-h-[44px]
                      ${on ? "active text-white" : "text-white/40 hover:text-white/70 hover:bg-white/5"}
                      ${!forMobile && collapsed ? "justify-center" : ""}
                    `}
                  >
                    <Icon name={item.icon} size={15} color={on ? "#fff" : undefined} strokeWidth={on ? 2 : 1.6} className="flex-shrink-0" />
                    {(forMobile || !collapsed) && (
                      <span className={`flex-1 text-xs font-semibold ${on ? "text-white" : "text-white/50"}`}>{t(item.label)}</span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* User footer */}
      {/* {(forMobile || !collapsed) && (
        <div className="border-t border-white/5 p-3">
          <div
            className="flex items-center gap-2.5 px-2 py-2 rounded-xl hover:bg-white/5 cursor-pointer transition-all min-h-[44px]"
            onClick={onLogout}
          >
            <Avatar name={userName} size={30} />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-white/80 truncate">{userName}</div>
              <div className="text-sm text-white/30">{t("role_" + (user?.roles_list?.[0] || "member"))}</div>
            </div>
            <Icon name="logout" size={12} className="text-white/30 flex-shrink-0" />
          </div>
        </div>
      )} */}
    </nav>
  );
};

export default function Sidebar({ active, onNav, collapsed, onLogout, mobileOpen, onMobileClose, user }) {
  const { language = 'en' } = useSettings();
  const t = (key) => translations[language]?.nav?.[key] || translations.en?.nav?.[key] || key;
  const { hasPermission } = usePermissions();
  const { hasFeature, isLoading } = useSubscription();

  const userName = user?.name || "User";
  const userRole = user?.primary_role_label || (user?.roles_list?.[0] ? user.roles_list[0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Member");

  const sharedProps = {
    t,
    collapsed,
    onNav,
    onMobileClose,
    userName,
    userRole,
    onLogout,
    active,
    hasPermission,
    hasFeature,
    user
  };

  if (isLoading) return null; // Or a skeleton loader

  return (
    <>
      {/* ── DESKTOP sidebar ── */}
      <div className="hidden lg:flex flex-shrink-0">
        <SidebarContent {...sharedProps} forMobile={false} />
      </div>

      {/* ── MOBILE drawer ── */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={onMobileClose}
      />
      <div
        className={`lg:hidden fixed left-0 top-0 h-full z-50 transition-transform duration-300 ease-in-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <SidebarContent {...sharedProps} forMobile={true} />
      </div>
    </>
  );
}
